<template>
  <Dialog
    v-if="canCreateFiles || !hideWhenDisabled"
    :open="open"
    @update:open="(value) => (open = value)"
    variant="create"
    background="white"
    :size="maxSmall ? 'full' : undefined"
    trackingId="files.new"
  >
    <slot :disabled="!canCreateFiles" />
    <template #header>
      <Component
        :is="maxSmall ? DrawerHeader : DialogHeader"
        :title="$t('components.files_upload_dialog.title')"
        :description="
          maxSmall
            ? undefined
            : $t('components.files_upload_dialog.description')
        "
        class="max-md:border-b max-md:rounded-none"
      />
    </template>
    <template #content>
      <div
        class="md:w-[520px] max-w-full flex flex-col grow p-4 md:px-6 md:mt-6 md:bg-secondary border-b md:border-y border-secondary overflow-y-hidden"
      >
        <BuildingSelectModal
          variant="create"
          :title="$t('components.files_upload_dialog.file_destination')"
          :description="
            $t('components.files_upload_dialog.building_select_description')
          "
          :teleport="false"
          :open="!!!buildingId"
          @update:open="(value) => handleBuildingSelectOpen(value)"
          v-model="selectedBuilding"
          :filterFn="
            (building) =>
              hasPermission(permissions.readDocument, building.id) &&
              hasPermission(permissions.createDocument, building.id)
          "
        >
          <div
            class="py-2 px-3 border border-secondary rounded-md w-full ltr:text-left rtl:text-right bg-primary hover:bg-tertiary mb-2"
            :class="{
              'bg-utility-gray-100 [&_p]:!fg-disabled cursor-default':
                loading || !!buildingId,
            }"
            v-on="
              loading || !!buildingId
                ? { click: (e) => e.stopPropagation() }
                : {}
            "
          >
            <p class="text-xs text-quaternary mb-1">
              {{
                $t(
                  `components.files_upload_dialog.file_destination${
                    selectedBuilding?.id ? "" : "_required"
                  }`
                )
              }}
            </p>
            <div class="flex gap-2">
              <Indicator
                variant="building"
                size="xs"
                class="shrink-0 bg-quaternary fg-quinary"
              />
              <p class="grow font-medium text-primary">
                {{ selectedBuilding?.name }}
              </p>
              <Id
                v-if="selectedBuilding?.identifier"
                :label="selectedBuilding?.identifier"
                class="shrink-0"
                size="sm"
              />
            </div>
          </div>
        </BuildingSelectModal>

        <FileUpload
          :buildingId="buildingId"
          :maxSize="100 * 1024 * 1024"
          :files="files"
          :disabled="loading"
          :limit="50"
          multiple
          @update:files="(value) => (files = value)"
        />
      </div>
    </template>

    <template #footer>
      <div v-if="!loading" class="flex py-4 px-6 md:py-6 gap-3">
        <Button
          v-if="!maxSmall"
          variant="secondary"
          type="gray"
          class="grow md:basis-1/2"
          :size="maxSmall ? 'xl' : 'lg'"
          @click="open = false"
        >
          {{ $t("components.buttons.cancel") }}
        </Button>
        <Button
          variant="primary"
          class="grow md:basis-1/2"
          :size="maxSmall ? 'xl' : 'lg'"
          :disabled="!files.length"
          @click="uploadFiles"
        >
          {{ $t("admin.files.buttons.upload") }}
          {{ files.length > 0 ? `(${files.length})` : "" }}
        </Button>
      </div>
      <div v-else class="flex py-4 px-6 md:py-6">
        <Button
          variant="primary"
          class="grow"
          :size="maxSmall ? 'xl' : 'lg'"
          :disabled="!allFilesUploaded"
          :loading="loading && !allFilesUploaded"
          @click="open = false"
        >
          {{ $t("components.buttons.done") }}
        </Button>
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import axios from "axios";
import route from "ziggy-js";
import { ref, computed, watchEffect, inject, watch } from "vue";
import { storeToRefs } from "pinia";
import { debounce, isEmpty } from "lodash";
import { Id } from "@/SharedComponents/Badges";
import { Button } from "@/SharedComponents/Buttons";
import { Dialog, DialogHeader } from "@/SharedComponents/Dialog";
import { DrawerHeader } from "@/SharedComponents/Drawer";
import { Indicator } from "@/SharedComponents/Indicator";
import { FileUpload } from "@/SharedComponents/Inputs";
import { BuildingSelectModal } from "@/SharedComponents/Modals";
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { useFilters } from "@/Composables/useFilters";
import { useSites } from "@/Composables/useSites";

const emit = defineEmits(["update:open", "filesUploaded"]);
const emitter = inject("emitter");

const props = defineProps({
  open: { type: Boolean, default: undefined },
  buildingId: { type: String, required: false },
  spaceIds: { type: Array, default: () => [] },
  hideWhenDisabled: { type: Boolean, default: true },
});

const { maxSmall } = useBreakpoints();
const globalFilterStore = useFilters("global");
const { filterValues } = storeToRefs(globalFilterStore);
const { isLoading, data } = useSites();
const { hasPermission, permissions } = inject("permissions");

const open = ref(props.open);
watch(
  () => props.open,
  (value) => {
    open.value = value;
  }
);
const files = ref([]);
const loading = ref(false);
const selectedBuilding = ref({});

const buildings = computed(() => {
  if (isLoading.value) {
    return [];
  }

  return data.value.filter(
    (building) =>
      hasPermission(permissions.readDocument, building.id) &&
      hasPermission(permissions.createDocument, building.id)
  );
});

const buildingId = computed(() => {
  if (props.buildingId) {
    return props.buildingId;
  }
  if (filterValues.value.tenant) {
    return filterValues.value.tenant[0];
  }
  if (buildings.value.length === 1) {
    return buildings.value[0].id;
  }
  return null;
});

const canCreateFiles = computed(() => {
  if (!buildingId.value) {
    return !isEmpty(buildings.value);
  }
  return (
    hasPermission(permissions.readDocument, buildingId.value) &&
    hasPermission(permissions.createDocument, buildingId.value)
  );
});

const allFilesUploaded = computed(() => {
  return files.value.every((file) => file.uploadProgress >= 1);
});

const uploadFiles = () => {
  loading.value = true;

  files.value
    .filter((file) => file.uploadProgress === 0)
    .forEach((file) => {
      axios
        .post(
          route("files.store"),
          {
            ...file.data(),
            tenant_id: selectedBuilding.value.id,
            space_ids: props.spaceIds,
          },
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            onUploadProgress: (event) => {
              file.uploadProgress = event.progress;
              file.remove = false;
              file.loading = true;
            },
          }
        )
        .catch((error) => {
          file.clearErrors();
          if (error.response?.status === 422) {
            Object.keys(error.response.data.errors).forEach((key) => {
              file.setError(key, error.response.data.errors[key][0]);
            });
          }
          loading.value = false;
        })
        .finally(() => {
          file.uploadProgress = 1.1;
        });
    });
};

const handleBuildingSelectOpen = (value) => {
  if (!value && !selectedBuilding.value?.id) {
    open.value = false;
  }
};

watchEffect(() => {
  selectedBuilding.value =
    buildings.value.find((el) => el.id === buildingId?.value) || {};
});

watch(allFilesUploaded, () => emit("filesUploaded"));

watch(
  open,
  (value) => {
    if (!value)
      debounce(() => {
        selectedBuilding.value =
          buildings.value.find((el) => el.id === buildingId?.value) || {};
        files.value = [];
        loading.value = false;
      }, 300)();

    if (loading.value) {
      emitter.emit("reload-files");
    }
    emit("update:open", value);
  },
  { immediate: true }
);
</script>
